import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * <Label3D>: glyph text on a billboard-able group, shaped by the engine the 2D
 * Control Label uses (`r3f/controls/native/text/`). `LabelGlyphs` is `React.lazy`,
 * so the shaping engine and font stay out of the initial bundle. `pixel_size` is a
 * nested group scale, since the glyph geometry is in Godot px.
 */
import { lazy, useMemo, useRef } from 'react';
import { node3dGroupSpread, transformFromNode3DProperties } from '../../../r3f/nodeTransform';
import { useViewportMode } from '../../../r3f/contexts/ViewportModeContext';
import { useBillboard } from '../../../r3f/hooks/useBillboard';
import { useFixedSize } from '../../../r3f/hooks/useFixedSize';
import { PendingSuspense } from '../../../resources/PendingSuspense';
// This file imports nothing from the shaping engine, so the engine stays out of the
// static closure `r3f/nodes/index.ts` pulls eagerly.
const LabelGlyphs = lazy(() => import('./LabelGlyphs'));
/**
 * Marks the invisible bounds proxy, like `CsgPrimitive.tsx`'s `CSG_BOUNDS_PROXY`.
 * `frameSceneBounds.ts` unions mesh AABBs, and the lazy glyphs can land after
 * `CameraFit`'s last retry, so without a synchronous stand-in the fit ignores the label.
 */
const LABEL3D_BOUNDS_PROXY = { tscnBoundsProxy: true };
export function Label3D({ node, children }) {
    const { showLabels } = useViewportMode();
    const properties = node.properties;
    const transform = useMemo(() => transformFromNode3DProperties(properties), [properties]);
    const groupRef = useRef(null);
    // `label_3d.cpp:396` hands the flag to the same cached shader the billboard
    // mode does; both are per-frame effects on the label's own group. Fixed size first:
    // the billboard freezes the world matrix, so a scale set after it lands a frame late.
    useFixedSize(groupRef, properties.fixed_size, transform.scale);
    // Shared with Sprite3D, so both implement Godot's billboard modes identically.
    useBillboard(groupRef, properties.billboard);
    // On by default to match Godot (the ADR-0008 amendment). The Labels toggle
    // hides it, and a marker group still positions any children and stays selectable.
    if (!showLabels) {
        return (_jsx("group", { name: node.name, ...node3dGroupSpread(transform), children: children }));
    }
    return (_jsxs(_Fragment, { children: [_jsxs("group", { ref: groupRef, name: node.name, ...node3dGroupSpread(transform), userData: { billboardMode: properties.billboard, isLabel3D: true }, children: [_jsx("group", { scale: properties.pixel_size, children: _jsx(PendingSuspense, { children: _jsx(LabelGlyphs, { nodeRef: groupRef, properties: properties }) }) }), _jsx("mesh", { visible: false, userData: LABEL3D_BOUNDS_PROXY, children: _jsx("boxGeometry", { args: [0, 0, 0] }) })] }), children === undefined ? null : _jsx("group", { ...node3dGroupSpread(transform), children: children })] }));
}
//# sourceMappingURL=Component.js.map